import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth/auth-context'
import { mapRow } from '@/lib/db-utils'

// ---------------------------------------------------------------------------
// Shooter Tier Definitions (Section 59 of the Design Document)
// ---------------------------------------------------------------------------

export type ShooterTier = 'free' | 'casual' | 'competitor' | 'athlete'
export type ShooterSubscriptionStatus = 'active' | 'cancelled' | 'past_due' | 'trialing'

/**
 * Shooter subscription record from the database.
 *
 * Note: With RevenueCat Web Billing integration, this table is kept in sync
 * by the RC webhook. The `useRevenueCat()` hook is the primary source of
 * truth for the effective tier; this DB record serves as a fallback and
 * is used for RLS policies and server-side checks.
 */
export interface ShooterSubscription {
  id: string
  userId: string
  tier: ShooterTier
  status: ShooterSubscriptionStatus
  billingCycle: 'monthly' | 'annual'
  currentPeriodStart: string | null
  currentPeriodEnd: string | null
  cancelAtPeriodEnd: boolean
  cancelledAt: string | null
  stripeSubscriptionId: string | null
  stripeCustomerId: string | null
  createdAt: string
  updatedAt: string
}

/** Pricing per the design doc Section 59 */
export const SHOOTER_TIER_PRICING = {
  free: { monthly: 0, annual: 0 },
  casual: { monthly: 0.99, annual: 9.99 },
  competitor: { monthly: 2.99, annual: 29.99 },
  athlete: { monthly: 3.99, annual: 39.99 },
} as const

export const SHOOTER_TIER_LABELS: Record<ShooterTier, string> = {
  free: 'Free',
  casual: 'Casual',
  competitor: 'Competitor',
  athlete: 'Athlete',
}

export const SHOOTER_TIER_TAGLINES: Record<ShooterTier, string> = {
  free: 'Get started with the basics',
  casual: 'For the occasional shooter',
  competitor: 'For regular competitors',
  athlete: 'Unlimited access for serious shooters',
}

/**
 * Feature limits per tier. -1 means unlimited.
 * These constants are the single source of truth used by useFeatureGating.
 */
export const SHOOTER_TIER_LIMITS = {
  free: {
    armouryFirearms: 1,
    shootLogEntriesPerMonth: 1,
    competitionEntriesPerMonth: 0,
    ammoRoundsTracked: 100,
    handloads: false,
    walletPasses: false,
    chronograph: false,
    goodReasonTracker: false,
    statsLevel: 'none' as const,
  },
  casual: {
    armouryFirearms: 2,
    shootLogEntriesPerMonth: 5,
    competitionEntriesPerMonth: 0.5, // 1 every 2 months
    ammoRoundsTracked: 250,
    handloads: false,
    walletPasses: true,
    chronograph: false,
    goodReasonTracker: true,
    statsLevel: 'limited' as const,
  },
  competitor: {
    armouryFirearms: 5,
    shootLogEntriesPerMonth: 10,
    competitionEntriesPerMonth: 2,
    ammoRoundsTracked: 2500,
    handloads: true,
    walletPasses: true,
    chronograph: true,
    goodReasonTracker: true,
    statsLevel: 'limited' as const,
  },
  athlete: {
    armouryFirearms: -1,
    shootLogEntriesPerMonth: -1,
    competitionEntriesPerMonth: -1,
    ammoRoundsTracked: -1,
    handloads: true,
    walletPasses: true,
    chronograph: true,
    goodReasonTracker: true,
    statsLevel: 'full' as const,
  },
} as const

export type StatsLevel = 'none' | 'limited' | 'full'

export const SHOOTER_TIER_FEATURES: Record<ShooterTier, string[]> = {
  free: [
    'Personal shooting profile',
    'Club discovery & search',
    'Casual challenges',
    'Family accounts',
    'Calendar sync',
    '1 firearm in armoury',
    '1 shoot log entry/month',
    '100 rounds ammo tracking',
  ],
  casual: [
    'Everything in Free, plus:',
    '2 firearms in armoury',
    '5 shoot log entries/month',
    '1 competition entry every 2 months',
    'Basic personal stats & trends',
    '250 rounds ammo tracking',
    'Digital wallet passes',
    'Good reason usage tracker',
    'Future modules included as released',
  ],
  competitor: [
    'Everything in Casual, plus:',
    '5 firearms in armoury',
    '10 shoot log entries/month',
    '2 competition entries/month',
    'Discipline breakdown stats',
    '2,500 rounds ammo tracking',
    'Handload management',
    'Chronograph logging',
    'Future modules included as released',
  ],
  athlete: [
    'Everything in Competitor, plus:',
    'Unlimited firearms',
    'Unlimited shoot log entries',
    'Unlimited competition entries',
    'Full analytics & comparisons',
    'Unlimited ammo tracking',
    'Stage analysis & rolling averages',
    'Future modules included as released',
  ],
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UseShooterSubscriptionReturn {
  subscription: ShooterSubscription | null
  tier: ShooterTier
  limits: (typeof SHOOTER_TIER_LIMITS)[ShooterTier]
  loading: boolean
  error: string | null
  changeTier: (tier: ShooterTier, cycle: 'monthly' | 'annual') => Promise<void>
  createCheckoutSession: (
    tier: ShooterTier,
    billingCycle: 'monthly' | 'annual',
  ) => Promise<{ clientSecret: string }>
  cancelSubscription: () => Promise<void>
  reactivateSubscription: () => Promise<void>
  refresh: () => Promise<void>
}

export function useShooterSubscription(): UseShooterSubscriptionReturn {
  const { user } = useAuth()
  const [subscription, setSubscription] = useState<ShooterSubscription | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const isMounted = useRef(true)

  const tier: ShooterTier = subscription?.tier ?? 'free'
  const limits = SHOOTER_TIER_LIMITS[tier]

  const fetchSubscription = useCallback(async () => {
    if (!user) {
      setSubscription(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    const { data, error: err } = await supabase
      .from('shooter_subscriptions')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle()

    if (!isMounted.current) return

    if (err) {
      setError(err.message)
      setLoading(false)
      return
    }

    if (data) {
      setSubscription(mapRow<ShooterSubscription>(data))
    } else {
      setSubscription(null)
    }

    setLoading(false)
  }, [user])

  useEffect(() => {
    isMounted.current = true
    fetchSubscription()
    return () => {
      isMounted.current = false
    }
  }, [fetchSubscription])

  const changeTier = async (newTier: ShooterTier, cycle: 'monthly' | 'annual') => {
    if (!user) return

    const now = new Date().toISOString()
    const periodEnd = getNextPeriodEnd(cycle)

    if (subscription) {
      const { error: err } = await supabase
        .from('shooter_subscriptions')
        .update({
          tier: newTier,
          billing_cycle: cycle,
          status: 'active',
          cancel_at_period_end: false,
          cancelled_at: null,
          current_period_start: now,
          current_period_end: periodEnd,
          updated_at: now,
        })
        .eq('id', subscription.id)

      if (err) {
        setError(err.message)
        throw new Error(err.message)
      }
    } else {
      const { error: err } = await supabase.from('shooter_subscriptions').insert({
        user_id: user.id,
        tier: newTier,
        status: 'active',
        billing_cycle: cycle,
        current_period_start: now,
        current_period_end: periodEnd,
      })

      if (err) {
        setError(err.message)
        throw new Error(err.message)
      }
    }

    await fetchSubscription()
  }

  const createCheckoutSession = async (
    checkoutTier: ShooterTier,
    billingCycle: 'monthly' | 'annual',
  ): Promise<{ clientSecret: string }> => {
    const { data, error: invokeError } = await supabase.functions.invoke(
      'stripe-shooter-checkout',
      {
        body: { tier: checkoutTier, billingCycle },
      },
    )

    if (invokeError) {
      const message = invokeError.message || 'Failed to create checkout session'
      setError(message)
      throw new Error(message)
    }

    if (!data?.clientSecret) {
      const errorMsg = data?.error || 'No client secret returned'
      setError(errorMsg)
      throw new Error(errorMsg)
    }

    return { clientSecret: data.clientSecret }
  }

  const cancelSubscription = async () => {
    if (!subscription || !user) return

    const { error: err } = await supabase
      .from('shooter_subscriptions')
      .update({
        cancel_at_period_end: true,
        cancelled_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', subscription.id)

    if (err) {
      setError(err.message)
      throw new Error(err.message)
    }

    setSubscription((prev) =>
      prev ? { ...prev, cancelAtPeriodEnd: true, cancelledAt: new Date().toISOString() } : null,
    )
  }

  const reactivateSubscription = async () => {
    if (!subscription || !user) return

    const { error: err } = await supabase
      .from('shooter_subscriptions')
      .update({
        cancel_at_period_end: false,
        cancelled_at: null,
        status: 'active',
        updated_at: new Date().toISOString(),
      })
      .eq('id', subscription.id)

    if (err) {
      setError(err.message)
      throw new Error(err.message)
    }

    setSubscription((prev) =>
      prev ? { ...prev, cancelAtPeriodEnd: false, cancelledAt: null, status: 'active' } : null,
    )
  }

  return {
    subscription,
    tier,
    limits,
    loading,
    error,
    changeTier,
    createCheckoutSession,
    cancelSubscription,
    reactivateSubscription,
    refresh: fetchSubscription,
  }
}

function getNextPeriodEnd(cycle: 'monthly' | 'annual'): string {
  const now = new Date()
  if (cycle === 'annual') {
    now.setFullYear(now.getFullYear() + 1)
  } else {
    now.setMonth(now.getMonth() + 1)
  }
  return now.toISOString()
}
